import { uniq, uniqBy } from "lodash";
import moment from "moment/moment";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiFactory from "../api";
import { SOCKET_STATUS, chatEvents$, socketStatus$ } from "../api/webSocketRx";
import { MAIL, MESSAGE_STATUS, conversationType } from "../config/Constant";
import { useInfoUser } from "../store/UserStore";
import { openNotificationError, openNotificationSuccess } from "../utils/Utils";
import { useMenuContext } from "./MenuContext";

const MailContext = createContext(null);
export const useMailContext = () => {
  return useContext(MailContext);
};

export const MailProvider = ({ children }) => {
  const {
    selectedConversation,
    setSelectedConversation,
    conversationList,
    handleUpdateConversationList,
    messageSearch,
  } = useMenuContext();

  const { user } = useInfoUser();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchTopMessage, setIsFetchTopMessage] = useState(false);
  const [lastTopMessageTs, setLastTopMessageTs] = useState(null);
  const [firstBottomMessageTs, setFirstBottomMessageTs] = useState(null);

  const [bottomMessageList, setBottomMessageList] = useState([]);
  const [topMessageList, setTopMessageList] = useState([]);

  const [totalNotificationItem, setTotalNotificationItem] = useState(0);
  const [isReply, setIsReply] = useState(false);
  const [userList, setUserList] = useState([]);

  const [isMoreMessage, setIsMoreMessage] = useState(false);
  const [cCOrgList, setCCOrgList] = useState([]);
  const [memberOrgList, setMemberOrgList] = useState([]);

  const [isModalLabelGroup, setIsModalLabelGroup] = useState(false);
  const [isForwardAllMail, setIsForwardAllMail] = useState(false);

  const [title, setTitle] = useState("");
  const [isOpenModalDeleteConversation, setIsOpenModalDeleteConversation] =
    useState(false);

  const updateUserList = async (userIds) => {
    if (!selectedConversation?.conversationId) return;

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: userIds,
    });

    if (userResult?.status !== 200) {
      return openNotificationError(userResult?.message);
    }

    if (userResult?.data?.length) {
      setUserList((prevUserList) => {
        const combinedUsers = [...prevUserList, ...userResult.data];
        return uniqBy(combinedUsers, "userId");
      });
    }
  };

  const getListUserFromMail = async (userIds) => {
    if (!selectedConversation?.conversationId) return [];

    if (!userIds || userIds?.length === 0) return [];

    const userResult = await apiFactory.conversationApi.getMemberList({
      conversationId: selectedConversation?.conversationId,
      memberIds: uniq(userIds),
    });

    if (userResult?.status !== 200) {
      openNotificationError(userResult?.message);
      return [];
    }

    return userResult?.data || [];
  };

  const fetchBottomMessages = async (
    signal,
    totalItems = 0,
    topMessagesLength = 0,
  ) => {
    if (!selectedConversation?.conversationId) return;
    setIsLoading(true);

    const limit =
      totalItems - topMessagesLength < MAIL.BOTTOM_MESSAGE
        ? totalItems - topMessagesLength
        : MAIL.BOTTOM_MESSAGE;

    if (limit === 0) return;

    try {
      const response = await apiFactory.mailApi.getNotifications(
        {
          conversationId: selectedConversation?.conversationId,
          limit,
          search: messageSearch,
          direction: "ASC",
        },
        signal,
      );

      if (response?.status !== 200) {
        openNotificationError(response?.message);
        return;
      }

      const items = response?.data?.items || [];
      const sortedMessages = items?.sort(
        (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
      );

      setBottomMessageList(sortedMessages);
      setFirstBottomMessageTs(sortedMessages?.[0]?.createdAt);

      return response;
    } catch (error) {
      if (error?.name !== "AbortError") {
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTopMessages = async (signal) => {
    if (!selectedConversation?.conversationId) return;

    setIsLoading(true);

    try {
      const response = await apiFactory.mailApi.getNotifications(
        {
          conversationId: selectedConversation?.conversationId,
          limit: MAIL.TOP_MESSAGE,
          maxTs: moment(),
          search: messageSearch,
          direction: "DESC",
        },
        signal,
      );

      if (response?.status !== 200) {
        openNotificationError(response?.message);
        return;
      }

      const topMessages = response?.data?.items || [];

      setTotalNotificationItem(response?.data?.totalItems || 0);
      setLastTopMessageTs(topMessages?.[topMessages?.length - 1]?.createdAt);
      setTopMessageList(topMessages);
      setIsFetchTopMessage(true);

      let userIds = topMessages.map((item) => item?.senderId).filter(Boolean);

      const bottomResponse = await fetchBottomMessages(
        signal,
        response?.data?.totalItems || 0,
        topMessages?.length || 0,
      );

      const bottomMessages = bottomResponse?.data?.items || [];
      userIds = [
        ...userIds,
        ...bottomMessages.map((item) => item?.senderId).filter(Boolean),
      ];

      const users = await getListUserFromMail(userIds);
      setUserList([...(users || [])]);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateNotificationItem = (eventData) => {
    if (!selectedConversation?.conversationId) return;
    if (selectedConversation?.type !== conversationType.NOTIFICATION) return;
    if (!eventData?.mail) return;

    const { announcer, seenMessageTs, type } = eventData ?? {};

    const mail = eventData?.mail;

    const updateList = (setter, idx, newMail) =>
      setter((prev) => {
        const next = [...(prev || [])];
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            ...newMail,
            unreadCount: newMail?.unreadCount ?? next[idx]?.unreadCount ?? 0,
          };
        } else {
          next.unshift({ ...newMail, unreadCount: newMail?.unreadCount ?? 0 });
        }
        return next;
      });

    // SEEN_MESSAGE
    if (type === MESSAGE_STATUS.SEEN_MESSAGE && seenMessageTs) {
      const { conversationId, lastOldSeenTs } = seenMessageTs;
      if (conversationId !== selectedConversation?.conversationId) return;

      const lastOld = new Date(lastOldSeenTs);
      const seenUpdater = (list) =>
        (list || []).map((m) => {
          const unread = Math.max(m?.unreadCount || 0, 0);
          const created = new Date(m?.createdAt);
          const updated = lastOld > created ? unread : Math.max(unread - 1, 0);
          return { ...m, unreadCount: updated };
        });

      setBottomMessageList(seenUpdater);
      setTopMessageList(seenUpdater);
      return;
    }

    // các event khác phải có mail + đúng conversation
    const mailConversationId = mail?.conversationId;
    if (
      !mailConversationId ||
      mailConversationId !== selectedConversation?.conversationId
    )
      return;

    const findInList = (list) =>
      (list || []).findIndex(
        (m) =>
          (m?.mailId && mail?.mailId && m.mailId === mail.mailId) ||
          (mail?.requestUuid && m?.requestUuid === mail.requestUuid),
      );

    const bottom = bottomMessageList || [];
    const top = topMessageList || [];

    const idxBottom = findInList(bottom);
    const idxTop = idxBottom >= 0 ? -1 : findInList(top);

    const exists =
      idxBottom >= 0
        ? { set: setBottomMessageList, idx: idxBottom }
        : idxTop >= 0
          ? { set: setTopMessageList, idx: idxTop }
          : null;

    const setActive = setTopMessageList;

    const applyUpdate = (setter, idx) => updateList(setter, idx, mail);

    const isSendByMe =
      announcer?.userId && user?.userId
        ? announcer.userId === user.userId
        : false;

    // update item
    exists ? applyUpdate(exists.set, exists.idx) : applyUpdate(setActive, -1);

    // new item from other user -> ensure userList & total count
    if (!isSendByMe && !exists) {
      if (!userList?.some((u) => u?.userId === announcer?.userId)) {
        updateUserList([announcer.userId]);
      }
      setTotalNotificationItem((prev) => Number(prev || 0) + 1);
    }
  };

  // =========================
  // Actions
  // =========================
  const onDeleteMessageNotifications = async () => {
    try {
      const rs = await apiFactory.mailApi.deleteMessageNotifications({
        conversationId: selectedConversation?.conversationId,
      });

      if (rs?.status !== 200) {
        openNotificationError("Delete messages notification failed");
      } else {
        openNotificationSuccess("Delete messages notification successfully");

        handleUpdateConversationList(
          (conversationList || []).filter(
            (item) =>
              item?.conversationId !== selectedConversation?.conversationId,
          ),
        );
      }
    } catch (error) {
      openNotificationError("Something went wrong!");
    } finally {
      setSelectedConversation(null);
      navigate("/conversation");
    }
  };

  const onLoadMore = async () => {
    if (!selectedConversation?.conversationId) return;

    const res = await apiFactory.mailApi.getNotifications({
      conversationId: selectedConversation?.conversationId,
      limit: MAIL.MORE_MESSAGE,
      maxTs: lastTopMessageTs,
      minTs: firstBottomMessageTs,
      search: messageSearch,
      direction: "DESC",
    });

    if (res?.status !== 200) {
      openNotificationError(res?.message);
      return;
    }

    const items = res?.data?.items || [];
    const userIds = items?.map((item) => item?.senderId).filter(Boolean);

    const rsUsers = await getListUserFromMail(userIds);
    if (rsUsers?.length) {
      setUserList((prevUserList) => {
        const combinedUsers = [...prevUserList, ...rsUsers];
        return uniqBy(combinedUsers, "userId");
      });
    }

    const sortedMessages = [...items, ...(topMessageList || [])]?.sort(
      (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt),
    );

    setTopMessageList(sortedMessages);

    if (items?.length > 0) {
      setLastTopMessageTs(items?.[items?.length - 1]?.createdAt);
    }

    setIsMoreMessage(items?.length === MAIL.MORE_MESSAGE);
    setTotalNotificationItem(res?.data?.totalItems || 0);
  };

  useEffect(() => {
    setTopMessageList([]);
    setBottomMessageList([]);
    setTotalNotificationItem(0);
    setIsFetchTopMessage(false);

    const controller = new AbortController();
    fetchTopMessages(controller.signal);

    return () => controller.abort();
  }, [selectedConversation?.conversationId, messageSearch]);

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData) return;

      if (!selectedConversation?.conversationId) return;
      if (selectedConversation?.type !== conversationType.NOTIFICATION) return;

      const mail = eventData?.mail || eventData?.message;
      const mailConversationId =
        mail?.conversationId || eventData?.seenMessageTs?.conversationId;

      if (
        mailConversationId &&
        mailConversationId !== selectedConversation?.conversationId
      ) {
        return;
      }

      updateNotificationItem(eventData);
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const prevStatusRef = { current: null };

    const sub = socketStatus$.subscribe((s) => {
      if (!s || s.scope !== "chat") return;

      if (!selectedConversation?.conversationId) return;
      if (selectedConversation?.type !== conversationType.NOTIFICATION) return;

      const prev = prevStatusRef.current;
      prevStatusRef.current = s.status;

      // vừa reconnect xong -> refetch
      if (
        prev !== SOCKET_STATUS.CONNECTED &&
        s.status === SOCKET_STATUS.CONNECTED
      ) {
        const controller = new AbortController();
        fetchTopMessages(controller.signal);
        // không cần cleanup ở đây vì request nhỏ; nếu bạn muốn strict:
        // return () => controller.abort();
      }
    });

    return () => sub.unsubscribe();
  }, [selectedConversation?.conversationId]);

  const values = useMemo(
    () => ({
      userList,
      setUserList,
      getListUserFromMail,
      topMessageList,
      setTotalNotificationItem,
      totalNotificationItem,
      selectedConversation,
      setIsFetchTopMessage,
      bottomMessageList,
      setBottomMessageList,
      setIsReply,
      isReply,
      isMoreMessage,
      setIsMoreMessage,
      title,
      setTitle,
      cCOrgList,
      setCCOrgList,
      memberOrgList,
      setMemberOrgList,
      isFetchTopMessage,
      setTopMessageList,
      isModalLabelGroup,
      setIsModalLabelGroup,
      onDeleteMessageNotifications,
      setFirstBottomMessageTs,
      setLastTopMessageTs,
      onLoadMore,
      isLoading,
      setIsLoading,
      setIsOpenModalDeleteConversation,
      isOpenModalDeleteConversation,
      updateUserList,
      isForwardAllMail,
      setIsForwardAllMail,
    }),
    [
      totalNotificationItem,
      isFetchTopMessage,
      userList,
      topMessageList,
      selectedConversation,
      bottomMessageList,
      isReply,
      isMoreMessage,
      title,
      cCOrgList,
      memberOrgList,
      isModalLabelGroup,
      isLoading,
      isOpenModalDeleteConversation,
      isForwardAllMail,
    ],
  );

  return <MailContext.Provider value={values}>{children}</MailContext.Provider>;
};
